import React from 'react';
import { ArrowLeft, Share2, Plane, Hotel, Bus, Car, Compass, ShieldCheck, FileText, Check, MessageCircle } from 'lucide-react';
import { colors } from '../../../styles/tokens';
import * as v5 from '../../../styles/v5.styles';
import { gradients } from '../../../styles/v5.styles';

/**
 * Tela 4 — Resumo
 *
 * Entry point: NOME_COMPLETO. (serif, uppercase)
 * Trip meta abaixo (Raleway 400, slate)
 * Card "Incluído" + Card de Preço (Raleway 400, NÃO serif)
 * CTA: "Fechar essa viagem" (verbo + objeto concreto)
 *
 * ANTI-PATTERNS:
 * - NÃO usar serif no preço (é dado, não título)
 * - NÃO colocar closing message entre preço e CTA
 */
export default function StorytellingResume({
  quote,
  onBack,
  onShare,
  whatsappNumber = '5577988229531',
}) {
  const blocks = quote.blocks || [];

  // Agrupa blocos por tipo
  const groupedBlocks = blocks.reduce((acc, block) => {
    const type = block.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(block);
    return acc;
  }, {});

  const typeOrder = ['flight', 'bus', 'hotel', 'transfer', 'car', 'tour', 'insurance', 'passport', 'visa'];

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '');
  };

  const formatDateRange = (startDate, endDate) => {
    const start = formatDateShort(startDate);
    const end = formatDateShort(endDate);
    if (start && end) return `${start} a ${end}`;
    return start || '';
  };

  const formatPassengers = () => {
    const adults = quote.adults || 1;
    const children = quote.children || 0;
    const infants = quote.infants || 0;
    const parts = [];
    if (adults === 1) parts.push('1 adulto');
    else if (adults > 1) parts.push(`${adults} adultos`);
    if (children === 1) parts.push('1 criança');
    else if (children > 1) parts.push(`${children} crianças`);
    if (infants === 1) parts.push('1 bebê');
    else if (infants > 1) parts.push(`${infants} bebês`);
    return parts.join(', ') || '1 adulto';
  };

  const formatPrice = (value) =>
    (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  const getTypeIcon = (type) => {
    const p = { size: 16, strokeWidth: 2, color: colors.accent };
    switch (type) {
      case 'flight': return <Plane {...p} />;
      case 'hotel': return <Hotel {...p} />;
      case 'transfer': return <Bus {...p} />;
      case 'bus': return <Bus {...p} />;
      case 'car': return <Car {...p} />;
      case 'tour': return <Compass {...p} />;
      case 'insurance': return <ShieldCheck {...p} />;
      case 'passport': return <FileText {...p} />;
      case 'visa': return <ShieldCheck {...p} />;
      default: return <Compass {...p} />;
    }
  };

  const getTypeInfo = (type, items) => {
    switch (type) {
      case 'flight': {
        const sorted = [...items].sort((a, b) => new Date(a.date) - new Date(b.date));
        const outbound = sorted[0];
        const ret = sorted.length > 1 ? sorted[sorted.length - 1] : null;
        const origin = outbound?.details?.segments?.[0]?.origin_city || outbound?.title?.split('→')[0]?.trim() || 'Origem';
        const dest = outbound?.details?.segments?.[outbound?.details?.segments?.length - 1]?.destination_city || outbound?.title?.split('→')[1]?.trim() || 'Destino';
        return { label: 'Passagem aérea', description: `${origin} → ${dest} (${ret ? 'ida e volta' : 'somente ida'})` };
      }
      case 'hotel':
        return items.map(h => ({
          label: h.title || h.details?.name || 'Hotel',
          description: formatDateRange(h.details?.checkin_date || h.date, h.details?.checkout_date),
        }));
      case 'transfer': {
        const hasOut = items.some(t => t.details?.is_outbound !== false);
        const hasRet = items.some(t => t.details?.is_outbound === false) || items.length > 1;
        let trip = 'Aeroporto ↔ Hotel';
        if (hasOut && hasRet) trip = 'Aeroporto ↔ Hotel (ida e volta)';
        else if (hasOut) trip = 'Aeroporto → Hotel';
        else if (hasRet) trip = 'Hotel → Aeroporto';
        return { label: items.length > 1 ? 'Transfers' : 'Transfer', description: trip };
      }
      case 'bus': {
        const sorted = [...items].sort((a, b) => new Date(a.date) - new Date(b.date));
        const out = sorted[0];
        const ret = sorted.length > 1 ? sorted[sorted.length - 1] : null;
        const origin = out?.details?.departure_city || out?.title?.split('→')[0]?.trim() || 'Origem';
        const dest = out?.details?.arrival_city || out?.title?.split('→')[1]?.trim() || 'Destino';
        const cls = out?.details?.seat_class || '';
        return { label: 'Rodoviário', description: `${origin} → ${dest} (${ret ? 'ida e volta' : 'somente ida'})${cls ? ` • ${cls}` : ''}` };
      }
      case 'car': {
        const c = items[0];
        return { label: `Carro: ${c.title || c.details?.model || 'Veículo'}`, description: formatDateRange(c.details?.pickup_date || c.date, c.details?.dropoff_date) };
      }
      case 'tour':
        return items.map(t => ({ label: t.title || t.details?.name || 'Passeio', description: formatDateShort(t.date) }));
      case 'insurance': {
        const ins = items[0];
        const company = ins.details?.company || 'Seguro Viagem';
        const plan = ins.details?.plan_name || '';
        const cov = ins.details?.coverage_amount || 0;
        const cur = ins.details?.coverage_currency || 'USD';
        return { label: plan ? `${company} - ${plan}` : company, description: `Cobertura ${cur} ${cov.toLocaleString('pt-BR')}` };
      }
      case 'passport': {
        return { label: 'Emissão de Passaporte', description: 'Serviço completo — tudo incluso' };
      }
      case 'visa': {
        const visa = items[0];
        const interviewRequired = visa.details?.interview_required;
        const visaType = visa.details?.visa_type;
        let desc = 'Serviço completo — tudo incluso';
        if (visaType === 'new') desc = 'Primeira via — com entrevista';
        else if (!interviewRequired) desc = 'Renovação — sem entrevista';
        else desc = 'Renovação — com entrevista';
        return { label: 'Visto Americano', description: desc };
      }
      default:
        return { label: items.map(i => i.title).join(', '), description: '' };
    }
  };

  const handleWhatsApp = () => {
    const msg = `Olá! Vi a cotação para ${quote.destination} e gostei muito. Quero seguir com a reserva!`;
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Monta lista de itens
  const itemsToRender = [];
  typeOrder.forEach((type) => {
    const items = groupedBlocks[type];
    if (!items || items.length === 0) return;
    const info = getTypeInfo(type, items);
    if (Array.isArray(info)) info.forEach((item) => itemsToRender.push({ ...item, type }));
    else itemsToRender.push({ ...info, type });
  });

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: gradients.screen4.base,
      display: 'flex',
      flexDirection: 'column',
      padding: '32px 32px 44px',
      fontFamily: v5.screenBase.fontFamily,
    }}>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
        <button onClick={onBack} style={v5.iconBtn}>
          <ArrowLeft size={18} />
        </button>
        <button onClick={onShare} style={v5.iconBtn}>
          <Share2 size={18} />
        </button>
      </div>

      {/* Meta */}
      <div style={{ ...v5.screenMeta, marginBottom: '12px' }}>Sua viagem está pronta</div>

      {/* Entry point: nome completo */}
      <h1 style={{ ...v5.entryPoint, marginBottom: '0' }}>{quote.client_name}.</h1>

      {/* Trip meta */}
      <div style={v5.tripMeta}>
        {quote.destination}
        <span style={v5.tripMetaSeparator}>·</span>
        {formatDateShort(quote.start_date)} a {formatDateShort(quote.end_date)}
        <span style={v5.tripMetaSeparator}>·</span>
        {formatPassengers()}
      </div>

      {/* Card: Incluído */}
      <div style={v5.includesCard}>
        <div style={v5.cardLabel}>Incluído</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {itemsToRender.map((item, idx) => (
            <React.Fragment key={`${item.type}-${idx}`}>
              <div style={v5.includesItem}>
                <div style={v5.iconCircle}>{getTypeIcon(item.type)}</div>
                <div style={{ flex: 1 }}>
                  <p style={v5.itemTitle}>{item.label}</p>
                  {item.description && <p style={v5.itemRoute}>{item.description}</p>}
                </div>
                <div style={{ color: colors.accent, fontSize: '14px' }}>✓</div>
              </div>
              {idx < itemsToRender.length - 1 && (
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Card: Preço (RALEWAY, não serif!) */}
      <div style={v5.priceCard}>
        <p style={v5.priceLabel}>Valor total</p>
        <p style={v5.priceValue}>R$ {formatPrice(quote.final_price)}</p>
        <p style={v5.priceInstallment}>em até 10× sem juros</p>
      </div>

      {/* CTA: entre preço e CTA, NADA. Sem closing message. */}
      <button
        onClick={handleWhatsApp}
        style={{
          ...v5.ctaButton,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <MessageCircle size={18} strokeWidth={2} />
        Fechar essa viagem
      </button>
    </div>
  );
}
